import { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Globe, ShieldAlert, ShieldBan, MailWarning, X } from "lucide-react";

const fmtTs = (iso) => (iso ? new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—");

const TYPE_LABELS = {
  ip_watchlist_hit: { label: "Watchlist IP", cls: "bg-red-100 text-red-700" },
  email_domain_blocklisted: { label: "Blocked domain", cls: "bg-amber-100 text-amber-700" },
};

function ListManager({ title, icon: Icon, items, onAdd, onRemove, placeholder, testid }) {
  const [val, setVal] = useState("");
  return (
    <div data-testid={testid} className="bg-white shadow-[0_8px_30px_rgba(0,0,0,0.04)] rounded-2xl p-6">
      <p className="font-ui text-xs tracking-[0.2em] uppercase text-[#4A4A4A] flex items-center gap-2">
        <Icon size={14} className="text-[#C71E2E]" /> {title}
      </p>
      <div className="mt-4 flex gap-2">
        <Input data-testid={`${testid}-input`} value={val} onChange={(e) => setVal(e.target.value)} placeholder={placeholder} className="input-dark h-9 text-sm" onKeyDown={(e) => e.key === "Enter" && (onAdd(val), setVal(""))} />
        <Button data-testid={`${testid}-add`} size="sm" onClick={() => { onAdd(val); setVal(""); }} className="rounded-full bg-[#C71E2E] text-white hover:bg-[#A01825] font-ui shrink-0">Add</Button>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        {items.length === 0 && <p className="text-xs font-ui text-[#4A4A4A]">None yet.</p>}
        {items.map((item) => (
          <span key={item} data-testid={`${testid}-item-${item}`} className="inline-flex items-center gap-1.5 text-[11px] font-ui border border-[#E5DED5] bg-[#FAF6F0] rounded-full px-3 py-1 text-[#1A1A1A]">
            {item}
            <button data-testid={`${testid}-remove-${item}`} onClick={() => onRemove(item)} className="text-[#4A4A4A] hover:text-[#C71E2E] transition-colors" aria-label={`Remove ${item}`}>
              <X size={11} />
            </button>
          </span>
        ))}
      </div>
    </div>
  );
}

export default function FraudMap() {
  const [geos, setGeos] = useState([]);
  const [events, setEvents] = useState([]);
  const [ipWatchlist, setIpWatchlist] = useState([]);
  const [domainBlocklist, setDomainBlocklist] = useState([]);

  const load = () => {
    api.get("/admin/signup-geos").then((r) => setGeos(r.data)).catch(() => {});
    api.get("/admin/fraud/events").then((r) => setEvents(r.data)).catch(() => {});
    api.get("/admin/fraud/settings").then((r) => {
      setIpWatchlist(r.data.ip_watchlist || []);
      setDomainBlocklist(r.data.email_domain_blocklist || []);
    }).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const save = (ips, domains) =>
    api.put("/admin/fraud/settings", { ip_watchlist: ips, email_domain_blocklist: domains })
      .then(() => toast.success("Fraud Watch settings saved"))
      .catch((e) => toast.error(errMsg(e)));

  const addIp = (ip) => { if (ip.trim() && !ipWatchlist.includes(ip.trim())) { const next = [...ipWatchlist, ip.trim()]; setIpWatchlist(next); save(next, domainBlocklist); } };
  const removeIp = (ip) => { const next = ipWatchlist.filter((x) => x !== ip); setIpWatchlist(next); save(next, domainBlocklist); };
  const addDomain = (d) => { const v = d.trim().toLowerCase().replace(/^@/, ""); if (v && !domainBlocklist.includes(v)) { const next = [...domainBlocklist, v]; setDomainBlocklist(next); save(ipWatchlist, next); } };
  const removeDomain = (d) => { const next = domainBlocklist.filter((x) => x !== d); setDomainBlocklist(next); save(ipWatchlist, next); };

  const pins = geos.filter((g) => typeof g.lat === "number" && typeof g.lon === "number");
  const byCountry = pins.reduce((acc, g) => {
    acc[g.country] = (acc[g.country] || 0) + 1;
    return acc;
  }, {});
  const countries = Object.entries(byCountry).sort((a, b) => b[1] - a[1]);

  return (
    <div data-testid="fraud-map-card" className="mt-6 space-y-6">
      <div className="bg-white shadow-[0_8px_30px_rgba(0,0,0,0.04)] rounded-2xl p-6">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="font-ui text-xs tracking-[0.2em] uppercase text-[#4A4A4A] flex items-center gap-2">
            <Globe size={14} className="text-[#C71E2E]" /> Signup Origins — Fraud Watch
          </p>
          <p data-testid="fraud-map-count" className="text-sm font-ui text-[#4A4A4A]">
            {pins.length} geo-located signup{pins.length === 1 ? "" : "s"} · {countries.length} {countries.length === 1 ? "country" : "countries"}
          </p>
        </div>
        <div className="relative mt-4 rounded-xl overflow-hidden border border-[#E5DED5]">
          <img src="/assets/world-map.jpg" alt="World map of signup origins" className="w-full block" loading="lazy" />
          {pins.map((g) => (
            <div
              key={g.id}
              data-testid={`signup-pin-${g.id}`}
              className="absolute w-2.5 h-2.5 rounded-full bg-[#C71E2E] shadow-[0_0_10px_rgba(199,30,46,0.8)] hover:scale-150 transition-transform"
              style={{ left: `${((g.lon + 180) / 360) * 100}%`, top: `${((90 - g.lat) / 180) * 100}%`, transform: "translate(-50%, -50%)" }}
              title={`${g.name} · ${g.email} · ${g.country}`}
            />
          ))}
          {pins.length === 0 && (
            <p data-testid="fraud-map-empty" className="absolute inset-0 flex items-center justify-center text-sm font-ui text-[#4A4A4A] bg-white/60">
              Pins appear here as new accounts sign up.
            </p>
          )}
        </div>
        {countries.length > 0 && (
          <div data-testid="fraud-map-countries" className="mt-4 flex flex-wrap gap-2">
            {countries.map(([country, count]) => (
              <span key={country} data-testid={`fraud-country-${country}`} className="text-[11px] font-ui border border-[#E5DED5] bg-[#FAF6F0] rounded-full px-3 py-1 text-[#4A4A4A]">
                {country} · {count}
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ListManager testid="ip-watchlist" title="IP Watchlist" icon={ShieldBan} items={ipWatchlist} onAdd={addIp} onRemove={removeIp} placeholder="e.g. 203.0.113.10 — visits & signups from this IP trigger alerts" />
        <ListManager testid="domain-blocklist" title="Email Domain Blocklist" icon={MailWarning} items={domainBlocklist} onAdd={addDomain} onRemove={removeDomain} placeholder="e.g. tempmail.com — signups & bookings flagged" />
      </div>

      <div data-testid="fraud-events-card" className="bg-white shadow-[0_8px_30px_rgba(0,0,0,0.04)] rounded-2xl p-6">
        <p className="font-ui text-xs tracking-[0.2em] uppercase text-[#4A4A4A] flex items-center gap-2">
          <ShieldAlert size={14} className="text-[#C71E2E]" /> Recent Fraud Events
        </p>
        <div className="mt-4 space-y-2.5">
          {events.length === 0 && <p data-testid="fraud-events-empty" className="text-sm font-ui text-[#4A4A4A]">No fraud events recorded — watchlist hits and blocked-domain activity will appear here and trigger an instant email alert.</p>}
          {events.map((ev) => {
            const t = TYPE_LABELS[ev.type] || { label: ev.type, cls: "bg-gray-100 text-gray-700" };
            return (
              <div key={ev.id} data-testid={`fraud-event-${ev.id}`} className="flex flex-wrap items-center gap-2 text-sm font-ui border-b border-[#F5EFE6] last:border-0 pb-2.5">
                <span className={`text-[10px] font-semibold uppercase tracking-wide rounded-full px-2.5 py-0.5 ${t.cls}`}>{t.label}</span>
                <span className="text-[#1A1A1A] flex-1 min-w-[200px]">{ev.detail}</span>
                <span className="text-xs text-[#4A4A4A]">{fmtTs(ev.created_at)}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
